/**
 * A pult „Új vendég" ablaka (#168, Karlo §4).
 *
 * MIRE VALÓ. A helyszínen megjelenik valaki, aki nincs a listán — meghívott,
 * kísérő, sajtós, kiállítói személyzet. Eddig ilyenkor a kezelőnek át kellett
 * mennie a Jegyek oldalra, ott kiadni egy jegyet, visszajönni, megkeresni és
 * beléptetni. Négy képernyőváltás egy sorban álló ember előtt.
 *
 * MIT NEM TUD, SZÁNDÉKOSAN — ugyanaz a szabály, mint a `ScanResultModal`-nál:
 * nem tud a hálózatról, a nyomtatásról és a beléptetésről. A címke tartalmát is
 * a HÍVÓ állítja elő (`badge`), hogy az itt látott előnézet és a papírra kerülő
 * nyomat egyetlen forrásból származzon.
 *
 * MIÉRT NEM antd `Form`. A testvérablak (`ScanResultModal`) sima `useState`-tel
 * tartja a mezőit, mert az ÉLŐ ELŐNÉZET minden leütésre kell. Ugyanaz a két
 * ablak két űrlapkezeléssel két viselkedés lenne ugyanabban a sarokban — a
 * pultnál ez a fajta apró eltérés az, ami hibázáshoz vezet.
 */

import { LoginOutlined, PrinterOutlined, SaveOutlined, UserAddOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Input, Modal, Row, Select, Space, Tag, Typography } from 'antd';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BadgeContent } from '../config/badgePrint';
import { ATTENDEE_TYPE_LABEL_KEYS, ATTENDEE_TYPES, type AttendeeType } from '../i18n/attendeeType';
import BadgePreview from './BadgePreview';

const { Text } = Typography;

/** A kezelő által beírt vendég — még nem jegy, csak amit a pultnál mondtak. */
export interface NewGuestDraft {
  company: string;
  lastName: string;
  firstName: string;
  email: string;
  attendeeType?: AttendeeType;
  ticketTypeId?: string;
}

/**
 * A két gomb, Karlo terve szerint.
 *
 * KÜLÖN ÉRTÉK, nem egy `print: boolean`: a hívó a `saving` állapotban is ezt
 * kapja vissza, tehát a pörgő pontosan azon a gombon ül, amelyiket megnyomták.
 */
export type NewGuestAction = 'save' | 'save-print-check-in';

export interface SimilarGuest {
  key: string;
  name: string;
  ticketNumber: string;
  checkedIn: boolean;
}

interface NewGuestModalProps {
  open: boolean;
  /** Az esemény jegytípusai — a szerver ezt KÖTELEZŐEN kéri a kiadáshoz. */
  ticketTypes: Array<{ id: string; name: string }>;
  /**
   * A jegytípusok BETÖLTÉSE hasalt el — nem az, hogy nincs jegytípus.
   *
   * A kettő ugyanaz az üres lista, a teendő viszont ellentétes: az elsőre
   * újrapróbálni kell, a másodikra jegytípust létrehozni a beállításoknál. Egy
   * közös üzenet a kezelőt egy ép beállítást „javítani" küldené, hálózati hiba
   * után, a pultnál.
   */
  ticketTypesFailed: boolean;
  onRetryTicketTypes: () => void;
  /**
   * A jegylista betöltött-e — a duplikáció-ellenőrzés előfeltétele.
   *
   * Betöltés közben a lista üres, tehát a keresés NÉMÁN nem talál semmit, ami
   * megkülönböztethetetlen a valódi nemleges választól. A sárga doboz egész
   * értéke az, hogy a hiánya is jelent valamit — ezért ilyenkor kimondjuk,
   * hogy az ellenőrzés még nem áll készen.
   */
  listReady: boolean;
  /** A címke tartalma — egy forrás az előnézetnek és a nyomatnak. */
  badge: (draft: NewGuestDraft) => BadgeContent;
  /**
   * Whether this event's desk prints badges (#202). Off, there is no badge
   * preview and the second button saves and checks in, without printing.
   */
  printEnabled: boolean;
  /** A már regisztrált, hasonló nevűek — a hívó listájából. */
  findSimilar: (draft: NewGuestDraft) => SimilarGuest[];
  /** Melyik gomb dolgozik éppen; `null`, ha egyik sem. */
  saving: NewGuestAction | null;
  onCancel: () => void;
  onSubmit: (draft: NewGuestDraft, action: NewGuestAction) => void;
}

const NewGuestModal: React.FC<NewGuestModalProps> = ({ open, ...rest }) => (
  <Modal
    open={open}
    onCancel={rest.onCancel}
    // Az ablak élő matrica-előnézetet tartalmaz, ami egy iframe; bezárás után
    // bent hagyva az ELŐZŐ vendég címkéje maradna felépítve a memóriában. A
    // `destroyOnHidden` egyben a mezőket is kiüríti: a következő vendég nem
    // örökli az előző félig beírt adatait.
    destroyOnHidden
    // The caller puts focus in the search box; antd's restore would move it back to the
    // "New guest" button, and the next reader scan's Enter would open this window again.
    focusTriggerAfterClose={false}
    footer={null}
    width={720}
    title={
      <Space>
        <UserAddOutlined />
        {/* A cím a törzsből jön, hogy a fordítás egy helyen legyen. */}
        <NewGuestTitle />
      </Space>
    }
  >
    <NewGuestBody {...rest} />
  </Modal>
);

const NewGuestTitle: React.FC = () => {
  const { t } = useTranslation();
  return <>{t('checkin.newGuestTitle')}</>;
};

type NewGuestBodyProps = Omit<NewGuestModalProps, 'open'>;

function NewGuestBody({
  ticketTypes,
  ticketTypesFailed,
  onRetryTicketTypes,
  listReady,
  badge,
  printEnabled,
  findSimilar,
  saving,
  onCancel,
  onSubmit,
}: NewGuestBodyProps): React.ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<NewGuestDraft>({
    company: '',
    lastName: '',
    firstName: '',
    email: '',
    attendeeType: undefined,
    ticketTypeId: undefined,
  });
  /*
    A hibák CSAK az első beküldés után jelennek meg.

    Egy üres űrlap, ami rögtön három pirossal fogadja a kezelőt, azt tanítja,
    hogy a piros a normális állapot. A gomb megnyomása után viszont a piros
    valódi információ: pont azt mondja meg, mi hiányzik.
  */
  const [submitted, setSubmitted] = useState(false);

  const setField =
    <K extends keyof NewGuestDraft>(field: K) =>
    (value: NewGuestDraft[K]) =>
      setDraft((prev) => ({ ...prev, [field]: value }));

  /*
    AZ EGYETLEN JEGYTÍPUS LEVEZETETT ÉRTÉK, NEM TÁROLT.

    Tárolva a kezdőérték az ablak MEGNYITÁSAKOR dőlt el. Ha a kezelő
    hamarabb nyitotta ki, mint ahogy a jegytípusok beértek — a helyszíni wifin
    ez a szokásos —, a piszkozat üresen maradt, a törzs viszont a megérkezés
    után már nem legördülőt, hanem sima szöveget rajzolt: a mező „kötelező"
    lett úgy, hogy KITÖLTENI NEM LEHETETT. Az ablak beküldhetetlenné vált, és
    csak bezárás-újranyitás oldotta fel. Az átnézés találta meg; levezetve a
    kérdés meg sem születik.
  */
  const effective: NewGuestDraft = {
    ...draft,
    ticketTypeId: ticketTypes.length === 1 ? ticketTypes[0].id : draft.ticketTypeId,
  };
  const problems = validate(effective, ticketTypes);
  const similar = listReady ? findSimilar(effective) : [];

  const submit = (action: NewGuestAction) => {
    setSubmitted(true);
    if (Object.keys(problems).length > 0) return;
    onSubmit(effective, action);
  };

  // A `saving` a HÍVÓTÓL jön: amíg a jegy születik, egyik gomb sem nyomható
  // újra. Enélkül a második kattintás MÁSODIK jegyet adna ki ugyanannak az
  // embernek — pont az a hiba, amit a hasonló-név figyelmeztetés is véd.
  const busy = saving !== null;
  const error = (field: keyof NewGuestDraft) => (submitted ? problems[field] : undefined);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {ticketTypes.length === 0 &&
        /*
          A szerver a `ticketTypeId`-t KÖTELEZŐEN kéri (`CreateTicketBaseDto`),
          tehát jegytípus nélkül a kiadás 400-zal állna meg. Ezt a pultnál nem
          a szerver nyers angol hibájával kell megtudni, hanem előre, magyarul
          — és a HELYES teendővel: elhasalt betöltésre újrapróbálni, valóban
          hiányzó jegytípusra a beállításokhoz menni. A kettőt egy üzenet nem
          fedheti le.
        */
        (ticketTypesFailed ? (
          <Alert
            type="error"
            showIcon
            message={t('checkin.newGuestTicketTypesFailed')}
            action={
              <Button size="small" onClick={onRetryTicketTypes}>
                {t('checkin.newGuestRetry')}
              </Button>
            }
          />
        ) : (
          <Alert type="warning" showIcon message={t('checkin.newGuestNoTicketTypes')} />
        ))}

      {!listReady && (
        // Őszintébb, mint a néma hiány: a kezelő tudja, hogy a duplikáció-
        // ellenőrzés még nem mondott semmit — se igent, se nemet.
        <Alert type="info" showIcon message={t('checkin.newGuestListLoading')} />
      )}

      <Row gutter={16}>
        <Col span={12}>
          <Field label={t('checkin.columnCompany')} optional={t('checkin.scanCompanyOptional')}>
            <Input
              aria-label={t('checkin.columnCompany')}
              value={draft.company}
              onChange={(e) => setField('company')(e.target.value)}
            />
          </Field>
        </Col>
        <Col span={6}>
          <Field label={t('checkin.scanLastName')} error={error('lastName')}>
            <Input
              aria-label={t('checkin.scanLastName')}
              status={error('lastName') ? 'error' : undefined}
              value={draft.lastName}
              onChange={(e) => setField('lastName')(e.target.value)}
            />
          </Field>
        </Col>
        <Col span={6}>
          <Field label={t('checkin.scanFirstName')} error={error('firstName')}>
            <Input
              aria-label={t('checkin.scanFirstName')}
              status={error('firstName') ? 'error' : undefined}
              value={draft.firstName}
              onChange={(e) => setField('firstName')(e.target.value)}
            />
          </Field>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Field label={t('checkin.scanAttendeeType')} error={error('attendeeType')}>
            <Select
              aria-label={t('checkin.scanAttendeeType')}
              style={{ width: '100%' }}
              status={error('attendeeType') ? 'error' : undefined}
              placeholder={t('checkin.newGuestTypePlaceholder')}
              value={draft.attendeeType}
              onChange={setField('attendeeType')}
              options={ATTENDEE_TYPES.map((value) => ({
                value,
                label: t(ATTENDEE_TYPE_LABEL_KEYS[value]),
              }))}
            />
          </Field>
        </Col>
        <Col span={8}>
          <Field label={t('checkin.columnTicketType')} error={error('ticketTypeId')}>
            {ticketTypes.length === 1 ? (
              // Egyetlen jegytípus mellett nincs mit választani. A pultnál egy
              // kötelező, egyelemű legördülő csak egy fölösleges kattintás
              // lenne — de kiírjuk, mert a jegy ettől is függ.
              <div>
                <Text>{ticketTypes[0].name}</Text>
              </div>
            ) : (
              <Select
                aria-label={t('checkin.columnTicketType')}
                style={{ width: '100%' }}
                status={error('ticketTypeId') ? 'error' : undefined}
                placeholder={t('checkin.newGuestTicketTypePlaceholder')}
                value={effective.ticketTypeId}
                onChange={setField('ticketTypeId')}
                options={ticketTypes.map((type) => ({ value: type.id, label: type.name }))}
              />
            )}
          </Field>
        </Col>
        <Col span={8}>
          <Field
            label={t('checkin.newGuestEmail')}
            optional={t('checkin.scanCompanyOptional')}
            error={error('email')}
          >
            <Input
              aria-label={t('checkin.newGuestEmail')}
              status={error('email') ? 'error' : undefined}
              value={draft.email}
              onChange={(e) => setField('email')(e.target.value)}
            />
          </Field>
        </Col>
      </Row>

      {similar.length > 0 && (
        /*
          A HASONLÓ NEVŰEK. Nem tiltás, figyelmeztetés: a névrokonság valódi, és
          a kezelő az, aki az előtte álló embert látja. Amit meg tudunk mondani,
          az az, hogy van már ilyen nevű jegy — és hogy bent van-e, mert ha
          igen, akkor szinte biztosan ugyanarról az emberről van szó.
        */
        <Alert
          type="warning"
          showIcon
          message={t('checkin.newGuestSimilarTitle', { count: similar.length })}
          description={
            <Space direction="vertical" size={2}>
              {similar.map((row) => (
                <Text key={row.key}>
                  {row.name} · {row.ticketNumber}{' '}
                  {row.checkedIn ? (
                    <Tag color="success">{t('checkin.checkedIn')}</Tag>
                  ) : (
                    <Tag>{t('checkin.notCheckedIn')}</Tag>
                  )}
                </Text>
              ))}
              <Text type="secondary">{t('checkin.newGuestSimilarHint')}</Text>
            </Space>
          }
        />
      )}

      <NewGuestBadgePreview printEnabled={printEnabled} badge={() => badge(effective)} />

      <Row justify="end">
        <Space>
          <Button onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>
          {/*
            KÉT gomb, Karlo terve szerint. A „csak mentés" nem kényelmi
            változat: a pultnál előfordul, hogy a vendég még nem jött meg (a
            kollégája veszi át a szervezést), vagy elfogyott a címke. Ilyenkor a
            jegynek attól még léteznie kell, különben a lista nem tud róla.
          */}
          <Button
            icon={<SaveOutlined />}
            loading={saving === 'save'}
            disabled={busy || ticketTypes.length === 0}
            onClick={() => submit('save')}
          >
            {t('checkin.newGuestSaveOnly')}
          </Button>
          <Button
            type="primary"
            icon={primaryIcon(printEnabled)}
            loading={saving === 'save-print-check-in'}
            disabled={busy || ticketTypes.length === 0}
            onClick={() => submit('save-print-check-in')}
          >
            {t(primaryLabelKey(printEnabled))}
          </Button>
        </Space>
      </Row>
    </Space>
  );
}

/*
  The second button without a printer (#202) saves and checks in. Kept out of
  `NewGuestBody`, which sits at the cognitive complexity limit.
*/
function primaryIcon(printEnabled: boolean): React.ReactNode {
  return printEnabled ? <PrinterOutlined /> : <LoginOutlined />;
}

function primaryLabelKey(printEnabled: boolean) {
  return printEnabled ? 'checkin.newGuestSavePrintCheckIn' : 'checkin.newGuestSaveCheckIn';
}

/** The badge preview, shown only on a desk that prints (#202). */
function NewGuestBadgePreview({
  printEnabled,
  badge,
}: {
  printEnabled: boolean;
  badge: () => BadgeContent;
}): React.ReactElement | null {
  const { t } = useTranslation();
  if (!printEnabled) return null;
  return (
    <div>
      <Text type="secondary">{t('checkin.badgePreview')} · 60 × 40 mm</Text>
      <div style={{ marginTop: 8 }}>
        <BadgePreview content={badge()} />
      </div>
    </div>
  );
}

/**
 * A hibaüzenetek szótárkulcsai, SZŰKEN — nem `string`.
 *
 * A `t()` ebben a panelben csak létező kulcsot fogad el, tehát a szűk típus
 * nem pontosságkérdés: ez az, ami a fordítóval ellenőrizteti, hogy mindkét
 * felirat tényleg benne van a `hu.json`-ban és az `en.json`-ban is.
 */
type ProblemKey = 'checkin.newGuestRequired' | 'checkin.newGuestBadEmail';

/**
 * Amit a kezelő nem hagyhat ki.
 *
 * SzótárKULCSOKAT ad vissza, nem kész szöveget: a `t()` a megjelenítés helyén
 * szólal meg, így a szabály maga nyelvfüggetlen marad.
 */
function validate(
  draft: NewGuestDraft,
  ticketTypes: Array<{ id: string }>
): Partial<Record<keyof NewGuestDraft, ProblemKey>> {
  const problems: Partial<Record<keyof NewGuestDraft, ProblemKey>> = {};
  if (!draft.lastName.trim()) problems.lastName = 'checkin.newGuestRequired';
  if (!draft.firstName.trim()) problems.firstName = 'checkin.newGuestRequired';
  // Karlo terve: „cég opcionális, típus kötelező". Alapérték SZÁNDÉKOSAN nincs:
  // egy előre kiválasztott kategória azt jelentené, hogy a sietős kezelő
  // mindenkit ugyanabba sorol, és a névtábla harmadik sora hazudik.
  if (!draft.attendeeType) problems.attendeeType = 'checkin.newGuestRequired';
  if (ticketTypes.length > 0 && !draft.ticketTypeId) {
    problems.ticketTypeId = 'checkin.newGuestRequired';
  }
  /*
    Az e-mail cím OPCIONÁLIS, de ha van, alakilag helyesnek kell lennie.

    Nem a szerverre bízzuk: a DTO `@IsEmail()`-je az EGÉSZ kérést 400-zal
    utasítja el, tehát egy elgépelt cím miatt a jegy sem születne meg — a
    pultnál, sorban álló ember előtt, angol hibaüzenettel. Ez a szűrő csak azt
    az egy esetet fogja meg, a valódi bírálat a szerveré marad.
  */
  if (draft.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())) {
    problems.email = 'checkin.newGuestBadEmail';
  }
  return problems;
}

interface FieldProps {
  label: string;
  optional?: string;
  error?: ProblemKey;
  children: React.ReactNode;
}

/** Címke + mező + a kötelezőség jelzése, egyetlen helyen. */
function Field({ label, optional, error, children }: FieldProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div>
      <Text type="secondary">{label}</Text>
      {optional && <Text type="secondary"> · {optional}</Text>}
      {error && (
        <Text type="danger">
          {' · '}
          {t(error)}
        </Text>
      )}
      <div style={{ marginTop: 4 }}>{children}</div>
    </div>
  );
}

export default NewGuestModal;
